// src/pages/admin/ReportsPage.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth as useAuthContext } from '../../contexts/AuthContext';
import { ReportProvider, useReportContext } from '../../contexts/ReportContext';

// Import report components
import DateRangeSelector from '../../components/reports/DateRangeSelector';
import StaffSelector from '../../components/reports/StaffSelector';
import ReportTypeSelector from '../../components/reports/ReportTypeSelector';
import ReportPreview from '../../components/reports/ReportPreview';
import ReportActions from '../../components/reports/ReportActions';

// Map category URL param to i18n translation key
const CATEGORY_LABEL_KEYS = {
  'time-tracking': 'timeTracking',
  'vacation':      'vacation',
  'schedule':      'schedule',
  'sick-leave':    'sickLeave'
};

/**
 * Content component that uses the ReportContext
 */
const ReportPageContent = () => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const {
    initializeWithDepartment,
    generateReport,
    isGeneratingReport,
    errorMessage,
    clearError,
    departmentName,
    companyName,
    reportCategory
  } = useReportContext();

  const { departmentId } = useParams();
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize the report context with the department ID
  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeWithDepartment(departmentId);
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, [departmentId, initializeWithDepartment]);

  // Handle report generation
  const handleGenerateReport = () => {
    generateReport();
  };

  // Resolve translation key from active category, fallback to time-tracking
  const categoryLabelKey = CATEGORY_LABEL_KEYS[reportCategory] || 'timeTracking';

  // Show loading state
  if (isInitializing) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('loadingDepartmentData')}</p>
      </div>
    );
  }

  return (
    <div className="reports-page">
      <div className="reports-page-header">

        {/* Back navigation */}
        <button
          className="reports-back-button"
          onClick={() => navigate('/admin/reports')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
          >
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          <span>{t('reportsTitle')}</span>
        </button>

        <h1>{t('reports')}</h1>
        <div className="reports-page-subheader">
          <h2>{departmentName}</h2>
          <div className="company-name">{companyName}</div>
          <span className="category-badge">{t(categoryLabelKey)}</span>
        </div>
      </div>

      {/* Error message display */}
      {errorMessage && (
        <div className="error-message">
          <span>{errorMessage}</span>
          <button onClick={clearError} className="close-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      )}

      <div className="reports-configuration">
        <div className="report-section">
          <DateRangeSelector />
        </div>

        <div className="report-section">
          <StaffSelector />
        </div>

        <div className="report-section">
          <ReportTypeSelector />
        </div>
      </div>

      {/* Generate Report Button */}
      <div className="generate-report-wrapper">
        <button
          className="generate-report-button"
          onClick={handleGenerateReport}
          disabled={isGeneratingReport}
        >
          <div className="button-content">
            {isGeneratingReport ? (
              <div className="spinner-small"></div>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path d="M13 5.08c3.06.44 5.48 2.86 5.92 5.92h3.03c-.47-4.72-4.23-8.48-8.95-8.95v3.03zM18.92 13c-.44 3.06-2.86 5.48-5.92 5.92v3.03c4.72-.47 8.48-4.23 8.95-8.95h-3.03zM11 18.92c-3.39-.49-6-3.4-6-6.92s2.61-6.43 6-6.92V2.05c-5.05.5-9 4.76-9 9.95 0 5.19 3.95 9.45 9 9.95v-3.03z"/>
              </svg>
            )}
            <span>
              {isGeneratingReport ? t('generatingReport') : t('generateReport')}
            </span>
          </div>
        </button>
      </div>

      <div className="report-results-section">
        <ReportPreview />
        <ReportActions />
      </div>
    </div>
  );
};

/**
 * Main Reports Page component that wraps content with ReportProvider
 */
const ReportsPage = () => {
  const { currentUser, currentCompany } = useAuthContext();
  const { category } = useParams();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser || !currentCompany) {
      navigate('/admin/login');
    }
  }, [currentUser, currentCompany, navigate]);

  if (!currentUser || !currentCompany) {
    return null;
  }

  return (
    <ReportProvider reportCategory={category}>
      <ReportPageContent />
    </ReportProvider>
  );
};

export default ReportsPage;